import { OrderTotalsSummary } from '@app/components/various/OrderTotalsSummary';
import { OrderTotalsSummaryItem } from '@app/components/various/OrderTotalsSummary/OrderTotalsSummaryItem';
import { getCalculatedInvoiceStatus, getIsInvoiceFull } from '@app/logic/invoices';
import { useInvoiceDetails } from '@app/services/hooks/invoice/useInvoiceDetails';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { Invoice } from '@typings';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { InvoiceInfo } from '../InvoiceInfo';
import { InvoicePaymentForm } from '../InvoicePaymentForm';
import { InvoicePaymentInfo } from '../InvoicePaymentInfo';
import { InvoiceShippingAddress } from '../InvoiceShippingAddress';
import { InvoiceSummary } from '../InvoiceSummary';

export const InvoiceDetailsPage = () => {
  const { invoiceId, invoiceHash } = useParams<Invoice.InvoiceIdentifiers>();
  const { data: invoice, isLoading } = useInvoiceDetails({ invoiceHash, invoiceId });
  const { t } = useTranslation(['common', 'invoices', 'totals', 'shipping', 'payments', 'orders']);
  const isInvoiceFull = isDefined(invoice) && getIsInvoiceFull(invoice);

  const orderSummaryItems = React.useMemo(() => {
    if (!isInvoiceFull) {
      return [];
    }

    return [
      ...(isDefined(invoice.shippingCost) ? [{ name: t('shipping:shipping_cost'), value: invoice.shippingCost }] : []),
      ...(invoice.discountAsANumber > 0 ? [{ name: t('common:discount'), value: invoice.discount }] : []),
      { name: t('totals:total_no_tax'), value: invoice.grandTotalWithoutTax },
      ...invoice.taxes.map(({ taxPercent, totalPriceTax }) => ({
        name: t('common:tax_percent', { taxPercent }),
        value: totalPriceTax,
      })),
      { name: t('totals:total_with_tax'), value: invoice.grandTotal },
      ...(invoice.prepaidAsANumber > 0 ? [{ name: t('invoices:prepaid_amount'), value: `-${invoice.prepaid}` }] : []),
    ];
  }, [invoice, isInvoiceFull, t]);

  const invoiceIdentifiers = React.useMemo(() => {
    if (isDefined(invoiceId) && isDefined(invoiceHash)) {
      return { invoiceHash, invoiceId };
    }

    return null;
  }, [invoiceId, invoiceHash]);

  return (
    <div className="mx-auto w-full max-w-screen-xl px-4 pb-16 lg:px-8">
      {!isDefined(invoice) && !isLoading ?
        <div data-testid="invoicesFailureMessage" className="p-12 text-center">
          <p>{t('invoices:failed_to_load_invoice')}</p>
        </div>
      : <>
          <InvoiceInfo
            invoice={invoice}
            invoiceIdentifiers={invoiceIdentifiers}
            invoiceStatus={isDefined(invoice) ? getCalculatedInvoiceStatus(invoice) : undefined}
            isLoading={isLoading}
          />
          {isDefined(invoice) && !isInvoiceFull && <InvoicePaymentInfo invoice={invoice} />}
          {isInvoiceFull && (
            <div className="grid border-t border-neutral-90 pt-4 lg:grid-cols-[1fr_20rem] lg:gap-20">
              <div>
                <InvoiceSummary invoice={invoice} />
                <InvoiceShippingAddress address={invoice?.shippingAddress} isLoading={isLoading} />
              </div>
              <div className="mt-8 border-t border-neutral-90 pt-8 lg:mt-[1.125rem] lg:border-none lg:pt-0">
                <h3 className="mb-5 heading-3">{t('orders:order_summary')}</h3>
                <OrderTotalsSummary finalPrice={{ name: t('totals:total_due_with_tax'), value: invoice?.totalDue }}>
                  {orderSummaryItems.map(summaryRow => (
                    <OrderTotalsSummaryItem key={summaryRow.name} name={summaryRow.name} value={summaryRow.value} />
                  ))}
                </OrderTotalsSummary>
                <section>
                  <h3 className="mb-5 mt-8 heading-3">{t('payments:payment_methods')}</h3>
                  {!isEmpty(invoice.paymentMethods) && isDefined(invoiceIdentifiers) ?
                    <InvoicePaymentForm paymentMethods={invoice.paymentMethods} invoiceIdentifiers={invoiceIdentifiers} />
                  : <p className="body-2">{t('invoices:no_payment_method_available')}</p>}
                </section>
              </div>
            </div>
          )}
        </>
      }
    </div>
  );
};
